import type { DiscountProviderProfile } from "./types";

/**
 * The baseline: an ordinary Rista coupon partner with no offers leg.
 *
 * 🔴 **Every unregistered `metaId` gets this**, which is what makes a partner the merchant
 * enables tomorrow safe rather than a crash or a half-applied discount. It describes the
 * majority shape — a code that the coupon API validates, a discount written onto the bill, no
 * partner round trip, no OTP, nothing to unblock — and says "no" to every capability only a
 * specific partner has.
 *
 * ⚠️ `isExternalLoyalty: false` is the load-bearing default. A provider that is not declared
 * external never reaches the partner apply/verify path at all, so an unknown `metaId` degrades
 * to the plain coupon flow rather than sending a request no partner will answer.
 *
 * Today `reelo` and anything unlisted run on exactly this and have no file of their own. Give a
 * provider a profile only when it actually differs.
 */
export const defaultProfile: DiscountProviderProfile = {
  metaId: "default",

  isExternalLoyalty: false,
  requiresCouponUnblock: false,
  unblockRequiresReferenceAndPhone: false,

  offerCodeField: "discountCode",
  hasCouponModal: false,

  requiresOtpOnApply: false,
  honoursOfferOtpFlag: false,
  returnsItemLevelDiscounts: false,
  rollsBackUnappliedCoupon: false,
  sendsRequestId: false,

  // Only read once a partner is external and its apply asked for an OTP; the values below are
  // the offer-endpoint shape, which is the one a new partner is likeliest to speak.
  verifyChannel: "offer-verify",
  wrapsVerifyInLoyaltyInput: false,
  verifySuccessSignal: "message",
  stampsExternalAuthentication: true,
  referenceIdSource: "none",
};

/** Build a profile from the baseline. Only the deltas belong in a provider's file. */
export const defineProfile = (
  metaId: string,
  overrides: Partial<Omit<DiscountProviderProfile, "metaId">>,
): DiscountProviderProfile => ({ ...defaultProfile, metaId, ...overrides });
